import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import { useCreateUser, useUpdateUser, type User } from "@/api/users";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { Secret } from "./TemporaryPasswordDialog";

interface Props {
  /** Open in create mode (true) or closed (false) — used when user is null. */
  open: boolean;
  /** When set, the dialog is in edit mode for this user. */
  user: User | null;
  /** The backend rejects an admin removing their own admin flag. */
  isSelf: boolean;
  onClose: () => void;
  onCreated: (secret: Secret) => void;
}

export function UserDialog({ open, user, isSelf, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const isEdit = user != null;
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isStaff, setIsStaff] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const create = useCreateUser();
  const update = useUpdateUser();

  useEffect(() => {
    setEmail(user?.email ?? "");
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setIsStaff(user?.isStaff ?? false);
    setServerError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when a different row opens; a refetched user must not wipe in-flight edits
  }, [user?.id, open]);

  const isOpen = open || isEdit;
  const isPending = create.isPending || update.isPending;

  const close = () => {
    setServerError(null);
    onClose();
  };

  const handleSave = async () => {
    setServerError(null);
    const input = {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      isStaff,
    };
    try {
      if (user) {
        await update.mutateAsync({ id: user.id, ...input });
      } else {
        const created = await create.mutateAsync(input);
        if (created.temporaryPassword) {
          onCreated({
            email: created.email,
            password: created.temporaryPassword,
          });
        }
      }
      close();
    } catch (err) {
      setServerError(
        (err instanceof ApiError && err.error?.message) ||
          t("settings.users.error"),
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("settings.users.editTitle")
              : t("settings.users.createTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="user-email">{t("settings.users.email")}</Label>
            <Input
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="user-first-name">
              {t("settings.users.firstName")}
            </Label>
            <Input
              id="user-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="user-last-name">
              {t("settings.users.lastName")}
            </Label>
            <Input
              id="user-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="user-is-staff">{t("settings.users.admin")}</Label>
              <p className="text-muted-foreground text-sm">
                {t("settings.users.adminDescription")}
              </p>
            </div>
            <Switch
              id="user-is-staff"
              checked={isStaff}
              disabled={isSelf}
              onCheckedChange={setIsStaff}
            />
          </div>
          {serverError && (
            <p className="text-destructive text-sm">{serverError}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t("common.buttons.cancel")}
          </Button>
          <Button disabled={!email.trim() || isPending} onClick={handleSave}>
            {isEdit ? t("common.buttons.save") : t("common.buttons.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
